/**
 * Runtime Environment Configuration
 * 
 * This file handles the loading of environment variables.
 * Priority order:
 * 1. Runtime variables (window.env) - injected by env.sh in Docker/K8s
 * 2. Build-time variables (process.env) - used in local development
 * 3. Default fallbacks
 */

// Define the shape of our environment configuration
export interface EnvConfig {
  API_GATEWAY_URL: string;
  API_GATEWAY_ABS_URL: string;
  APP_ENV: 'development' | 'test' | 'staging' | 'production';
  APP_VERSION: string;
}

// Extend the global Window interface
declare global {
  interface Window {
    env?: Partial<EnvConfig>;
  }
}

/**
 * Helper to safely get environment variables with fallback
 */
const getEnvVar = <K extends keyof EnvConfig>(
  key: K,
  processEnvKey: string,
  defaultValue: EnvConfig[K]
): EnvConfig[K] => {
  // 1. Try runtime window.env
  if (window.env && window.env[key]) {
    return window.env[key] as EnvConfig[K];
  }

  // 2. Try build-time process.env
  if (process.env[processEnvKey]) {
    return process.env[processEnvKey] as EnvConfig[K];
  }

  // 3. Return default
  return defaultValue;
};

// --- Configuration Constants ---

// API URL - Crucial for connecting to the backend
export const API_GATEWAY_URL = getEnvVar(
  'API_GATEWAY_URL',
  'REACT_APP_API_URL',
  '/api'
);

// Absolute gateway URL — used when browser must POST directly to the gateway domain
// (e.g. OAuth2 login form, so the session cookie is sent to the correct domain)
export const API_GATEWAY_ABS_URL = getEnvVar(
  'API_GATEWAY_ABS_URL',
  'REACT_APP_API_GATEWAY_ABS_URL',
  '' as EnvConfig['API_GATEWAY_ABS_URL']
);

// App Environment - Determines feature flags and logging
export const APP_ENV = getEnvVar(
  'APP_ENV',
  'REACT_APP_ENV',
  'development'
);

// App Version - Useful for tracking which version is deployed
export const APP_VERSION = getEnvVar(
  'APP_VERSION',
  'REACT_APP_VERSION',
  '1.0.0'
);

// Consolidated Config Object
export const ENV_CONFIG = {
  apiGatewayUrl: API_GATEWAY_URL,
  apiGatewayAbsUrl: API_GATEWAY_ABS_URL,
  appEnv: APP_ENV,
  appVersion: APP_VERSION,

  // Helpers
  isDevelopment: APP_ENV === 'development',
  isTest: APP_ENV === 'test',
  isStaging: APP_ENV === 'staging',
  isProduction: APP_ENV === 'production'
};

export default ENV_CONFIG;
